import { useEffect, useRef, useState } from 'react';

export function useContainerRef<T>(initialValue: T): React.MutableRefObject<T>;
export function useContainerRef<T>(initialValue: T | null): React.RefObject<T>;

/**
 * Use a `ref` that rerenders a component exactly once when it mounts. Useful for components
 * that render tooltips/popovers into a specific container `ref`. On first render, `ref.current`
 * gets passed into child components as its default value (i.e., `null`). It's only after this
 * first render that `ref.current` gets assigned to the actual DOM element.
 *
 * By forcing a rerender, child components that depend on the container `ref` will receive
 * the correct value on the second render which hopefully resolves any positioning issues
 * before users interact with the UI element.
 */
export function useContainerRef<T>(initialValue: T | null) {
  const [, setValue] = useState(0);
  useEffect(() => setValue((v) => v + 1), []);
  return useRef<T>(initialValue);
}
